import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  RouterContextProvider,
  useLoaderData,
} from "react-router";
import {
  CertificateCreationRequest,
  CertificateRepresentation,
  CertificateRevocationRequest,
  CertificateUploadRequest,
  CreatedCertificate,
  OAuthClientRepresentation,
} from "../infrastructure/api/api";
import { NyvaError } from "../infrastructure/errors/errors";
import { organizationContext } from "../infrastructure/organization/OrganizationContext";
import { rootPath } from "../infrastructure/translation/useRootPath";
import { clientsApi } from "../Client/clientsApi";
import { certificatesApi } from "./certificatesApi";

const certificates = (context: Readonly<RouterContextProvider>) =>
  certificatesApi(context.get(organizationContext));

interface CertificatesListRouteData {
  certificates: CertificateRepresentation[];
  clients: OAuthClientRepresentation[];
}

export const certificatesListLoader = async ({
  context,
}: LoaderFunctionArgs): Promise<CertificatesListRouteData> => {
  const organization = context.get(organizationContext);
  const [certificateList, clients] = await Promise.all([
    certificatesApi(organization).listCertificates(),
    clientsApi(organization).listClients(),
  ]);

  return { certificates: certificateList, clients };
};

export const useCertificatesList = (): CertificatesListRouteData =>
  useLoaderData<CertificatesListRouteData>();

interface CertificateRouteData {
  certificate: CertificateRepresentation | undefined;
}

export const certificateLoader = async ({
  params,
  context,
}: LoaderFunctionArgs): Promise<CertificateRouteData> => ({
  certificate: await certificates(context).findCertificate(
    params.certificateId!,
  ),
});

export const useCertificate = (): CertificateRepresentation | undefined =>
  useLoaderData<CertificateRouteData>().certificate;

const revokeCertificate = async ({
  params,
  request,
  context,
}: ActionFunctionArgs) => {
  const { reason }: CertificateRevocationRequest = await request.json();

  try {
    await certificates(context).revokeCertificate(
      params.certificateId!,
      reason,
    );
    return null;
  } catch (err) {
    console.error("Failed to revoke certificate:", err);
    return { revokeFailed: true };
  }
};

const unregisterCertificate = async ({
  params,
  context,
}: ActionFunctionArgs) => {
  try {
    await certificates(context).unregisterCertificate(params.certificateId!);
    return redirect(
      `${rootPath(params.language)}/organization/${params.orgnr}`,
    );
  } catch (err) {
    console.error("Failed to unregister certificate:", err);
    return { unregisterFailed: true };
  }
};

export const certificateAction = (args: ActionFunctionArgs) =>
  args.request.method === "DELETE"
    ? unregisterCertificate(args)
    : revokeCertificate(args);

export type CreateCertificateResult =
  | { created: CreatedCertificate }
  | { uploaded: CertificateRepresentation }
  | { error: string };

const errorMessage = (err: unknown): string =>
  (err instanceof NyvaError && err.apiErrorMessage) ||
  (err instanceof Error ? err.message : "Unknown error");

export const createCertificateAction = async ({
  request,
  context,
}: ActionFunctionArgs): Promise<CreateCertificateResult> => {
  const body: CertificateCreationRequest | CertificateUploadRequest =
    await request.json();

  try {
    if ("certificate" in body) {
      return { uploaded: await certificates(context).uploadCertificate(body) };
    }

    return { created: await certificates(context).createCertificate(body) };
  } catch (err) {
    console.error("Failed to create certificate:", err);
    return { error: errorMessage(err) };
  }
};
